/**
 * Phase 134C — First Stream Celebration Banner
 * --------------------------------------------------------------------
 * Rendered on /livex/dashboard the FIRST time an academy_owner returns
 * to the dashboard after broadcasting their first stream. One-shot
 * via localStorage key `livex_first_stream_celebrated_{academy_id}`.
 *
 * Strategic intent: reinforce emotional success at the activation moment.
 * The "aha" moment is the first stream — surface it loudly and warmly,
 * then it never appears again. Stays out of the way for active streamers.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, X, History, Sparkles } from 'lucide-react';
import '@/styles/livex.css';

export default function LivexFirstStreamCelebration({ academyId, onDismiss }) {
  // Don't render at all if we can't tie the dismissal to a specific academy —
  // otherwise an anonymous fallback localStorage key could "stick" across
  // sessions on shared devices.
  const storageKey = academyId ? `livex_first_stream_celebrated_${academyId}` : 'livex_first_stream_celebrated_anon';
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(storageKey) === '1'; } catch { return false; }
  });
  if (!academyId) return null;
  if (dismissed) return null;
  const handleClose = () => {
    try { localStorage.setItem(storageKey, '1'); } catch { /* ignore */ }
    setDismissed(true);
    onDismiss?.();
  };
  return (
    <div
      data-testid="livex-dashboard-first-stream-celebration"
      className="relative p-5 sm:p-6 rounded-2xl border-2 overflow-hidden livex-burst"
      style={{
        background: 'linear-gradient(135deg, #052e2a 0%, #0c1a2e 60%)',
        borderColor: '#10b981',
        boxShadow: '0 12px 36px rgba(16,185,129,0.28)',
      }}
    >
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(55% 60% at 0% 50%, rgba(16,185,129,0.20), transparent 70%)' }}
      />
      <button
        onClick={handleClose}
        aria-label="Dismiss first stream celebration"
        data-testid="livex-dashboard-first-stream-celebration-close"
        className="absolute top-3 right-3 inline-flex items-center justify-center w-8 h-8 text-emerald-200/70 hover:text-white hover:bg-emerald-900/40 rounded-lg transition-colors"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="relative flex items-start gap-4 flex-wrap">
        <div
          className="flex-shrink-0 inline-flex items-center justify-center w-14 h-14 rounded-2xl"
          style={{
            background: 'linear-gradient(135deg, #10b981, #14b8a6)',
            boxShadow: '0 10px 24px rgba(16,185,129,0.40)',
          }}
        >
          <Trophy className="h-7 w-7 text-white" strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-black uppercase tracking-[0.2em] text-emerald-300 mb-1 flex items-center gap-1.5">
            <Sparkles className="h-3 w-3" /> Activated
          </div>
          <h3 className="text-xl sm:text-2xl font-black text-white tracking-tight leading-tight pr-8">
            🎉 Your academy is officially live on StreamX!
          </h3>
          <p className="livex-body-text mt-1.5" style={{ fontSize: '14px' }}>
            You just unlocked the most important moment in the StreamX journey — your
            first broadcast. Every stream from here on builds your academy's digital
            archive, parent loyalty, and scouting visibility.
          </p>
          <Link
            to="/streamx/history"
            data-testid="livex-dashboard-first-stream-celebration-cta"
            className="inline-flex items-center gap-2 mt-3 px-4 py-2.5 text-sm font-bold text-white bg-emerald-700 hover:bg-emerald-600 border-2 border-emerald-600 rounded-lg transition-all"
          >
            <History className="h-4 w-4" /> View your stream history
          </Link>
        </div>
      </div>
    </div>
  );
}
